// src/lib/forms-api.js — Phase 2.E.12 (ADR-045)
// Helpers pour les formulaires publics : génération de tokens admin
// + chargement public d'un token + submission publique.

import { supabase } from "../supabase"
import { resolveAppBaseUrl } from "./app-base-url"

// ── Côté admin : génération d'un token ─────────────────────────────

export async function createFormToken({ type, logement_id, reservation_id = null, expires_in_days = 14, created_by = null }) {
  // 'unified' (ADR-065 PR 5) = nouveau format fusion welcome+checkin.
  // 'welcome' / 'checkin' conservés pour rétrocompat tokens existants.
  if (!type || !['unified', 'welcome', 'checkin'].includes(type)) throw new Error("type invalide (unified | welcome | checkin)")
  if (!logement_id) throw new Error("logement_id requis")

  const expires_at = expires_in_days ? new Date(Date.now() + expires_in_days * 86400000).toISOString() : null
  const { data, error } = await supabase
    .from("form_tokens")
    .insert({ type, logement_id, reservation_id, expires_at, created_by })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function listFormTokens(logement_id) {
  const { data, error } = await supabase
    .from("form_tokens")
    .select("token, type, logement_id, reservation_id, expires_at, used_at, created_at, created_by")
    .eq("logement_id", logement_id)
    .order("created_at", { ascending: false })
    .limit(50)
  if (error) throw error
  return data || []
}

// ADR-074 — révocation d'un token public par l'admin.
// La policy admin_all (migration 20260504000005) autorise déjà DELETE pour
// l'admin authentifié. Pas de migration RLS nécessaire.
export async function deleteFormToken(token) {
  if (!token) throw new Error("Token requis")
  const { error } = await supabase.from("form_tokens").delete().eq("token", token)
  if (error) throw error
  return true
}

// Fiches d'arrivée LOT 3 (ADR-188) — liste les tokens formulaire rattachés à
// une réservation (OTA = reservations.id) OU un pricing_block (direct =
// pricing_blocks.id). La valeur stockée dans form_tokens.reservation_id
// correspond à l'item ouvert dans ResaV2Modal (col unique, pas de discriminant).
// Lecture admin seule (RLS admin_all). Borne .limit(50) cohérente.
export async function listFormTokensByReservation(reservation_id) {
  if (!reservation_id) return []
  const { data, error } = await supabase
    .from("form_tokens")
    .select("token, type, logement_id, reservation_id, expires_at, used_at, created_at")
    .eq("reservation_id", reservation_id)
    .order("created_at", { ascending: false })
    .limit(50)
  if (error) throw error
  return data || []
}

export async function listFormSubmissions(token_id) {
  // RGPD : on NE sélectionne JAMAIS ip_address — elle est collectée côté
  // anti-abus mais ne doit jamais remonter à l'admin (ADR-188 LOT 1).
  // .limit(50) : même borne que listFormTokens (garde-fou volume).
  const { data, error } = await supabase
    .from("form_submissions")
    .select("id, token_id, payload, submitted_at")
    .eq("token_id", token_id)
    .order("submitted_at", { ascending: false })
    .limit(50)
  if (error) throw error
  return data || []
}

// ── N2-IA LOT 2 (ADR-189) — pièces d'identité côté admin ───────────
// État réel des pièces depuis id_documents (RLS admin_select : SELECT réservé au
// rôle admin authentifié). Une ligne par (token_id, ref). On ne sélectionne
// JAMAIS extracted_name (PII pièce d'identité — affiché seulement LOT 3/4 Vision).
// .limit(50) : garde-fou volume (≤ 3 refs × tokens d'une résa, marge large).
export async function listIdDocumentsByTokens(tokenIds) {
  const ids = (Array.isArray(tokenIds) ? tokenIds : []).filter(Boolean)
  if (ids.length === 0) return []
  // `deleted_at` (lot RGPD purge 14 j) : une ligne purgée s'affiche « Purgé
  // (RGPD) » côté admin (badge dédié, bouton Voir masqué — l'objet Storage
  // n'existe plus). Rétrocompat : tant que la migration 20261024000001 n'est
  // pas appliquée, la colonne est absente (42703) → retry SANS deleted_at.
  const colsWithDeleted =
    "token_id, ref, status, name_match, fail_reason, updated_at, deleted_at"
  const colsLegacy = "token_id, ref, status, name_match, fail_reason, updated_at"
  let { data, error } = await supabase
    .from("id_documents")
    .select(colsWithDeleted)
    .in("token_id", ids)
    .limit(50)
  if (error && error.code === "42703") {
    ;({ data, error } = await supabase
      .from("id_documents")
      .select(colsLegacy)
      .in("token_id", ids)
      .limit(50))
  }
  if (error) {
    // Table absente (migration non appliquée) = service inerte → liste vide, pas
    // d'erreur bloquante (le fallback stub payload reste affiché).
    if (error.code === "42P01") return []
    throw error
  }
  return data || []
}

// Consultation sécurisée d'une pièce : action serveur ADMIN (requireAdmin) qui
// renvoie une signed download URL TTL 120s. Jamais persistée côté client (on
// l'ouvre puis on l'oublie). Auth = JWT admin (Bearer).
export async function idDocDownloadUrl(token_id, ref) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error("not_authenticated")
  const res = await fetch(`/api/beds24-mapping?action=id-doc-download-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ token_id, ref }),
  })
  let body = null
  try { body = await res.json() } catch (_) { /* non-JSON */ }
  if (!res.ok || !body || body.ok !== true || !body.signed_url) {
    throw new Error((body && body.error) || `http_${res.status}`)
  }
  return body.signed_url
}

// Consultation du PDF du CONTRAT SIGNÉ : action serveur ADMIN (requireAdmin) qui
// renvoie une signed download URL TTL 120s. Jamais persistée côté client (on
// l'ouvre puis on l'oublie). Le path est dérivé serveur-side de la DBR liée à
// l'item (reservation/pricing_block) — jamais un input client.
// Retourne null si le contrat n'est pas (encore) signé/archivé (404 not_signed)
// pour permettre à l'UI d'afficher l'état « absent » sans erreur bloquante ;
// throw sur les autres erreurs.
export async function contractDownloadUrl(item_id, item_kind) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error("not_authenticated")
  const res = await fetch(`/api/beds24-mapping?action=contract_download_url`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ item_id, item_kind }),
  })
  let body = null
  try { body = await res.json() } catch (_) { /* non-JSON */ }
  // Contrat pas (encore) signé/archivé → null (état « absent », pas une erreur).
  if (res.status === 404 && body && (body.error === "not_signed" || body.error === "not_found")) {
    return null
  }
  if (!res.ok || !body || body.ok !== true || !body.signed_url) {
    throw new Error((body && body.error) || `http_${res.status}`)
  }
  return body.signed_url
}

// ── CHANTIER ÉTAT DES LIEUX (EDL, vue admin lecture seule v1) ─────────
// Soumission d'état des lieux d'arrivée d'une résa (OTA = reservations.id,
// direct = pricing_blocks.id — même convention polymorphique que form_tokens).
// Action serveur ADMIN (requireAdmin) : renvoie la soumission + photos en
// signed URL TTL 120s (bucket privé edl-photos — jamais persistées côté client).
// Retours :
//   { submission: {...} | null }        — service actif ;
//   { unavailable: true }               — 503 migration non appliquée (l'UI
//     affiche « module inactif », jamais un faux « aucune soumission »).
export async function edlAdminGet(reservation_id) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error("not_authenticated")
  const res = await fetch(`/api/beds24-mapping?action=edl_admin_get`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ reservation_id }),
  })
  let body = null
  try { body = await res.json() } catch (_) { /* non-JSON */ }
  if (res.status === 503) return { unavailable: true, submission: null }
  if (!res.ok || !body || body.ok !== true) {
    throw new Error((body && body.error) || `http_${res.status}`)
  }
  return { unavailable: false, submission: body.submission || null }
}

// Relance la vérification d'une pièce 'failed' → 'received' (reset verdict).
// Action serveur ADMIN. Seule voie de re-vérification après verdict (C4).
export async function idDocRecheck(token_id, ref) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error("not_authenticated")
  const res = await fetch(`/api/beds24-mapping?action=id-doc-recheck`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ token_id, ref }),
  })
  let body = null
  try { body = await res.json() } catch (_) { /* non-JSON */ }
  if (!res.ok || !body || body.ok !== true) {
    throw new Error((body && body.error) || `http_${res.status}`)
  }
  return true
}

// « Rouvrir » la fiche d'arrivée figée d'une réservation (décision Dimitri
// 03/10) : action ADMIN form_rouvrir (requireAdmin au routeur). Rend la
// réponse du serveur telle quelle : { ok, ecrit, rouvert_jusqu_au } ou
// { ok, deja_modifiable, jusqu_au } ; lève une Error portant `code` sinon.
export async function rouvrirFormulaire(reservation_id) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error("not_authenticated")
  const res = await fetch(`/api/beds24-mapping?action=form_rouvrir`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ reservation_id }),
  })
  let body = null
  try { body = await res.json() } catch (_) { /* non-JSON */ }
  if (!res.ok || !body || body.ok !== true) {
    const err = new Error((body && (body.message || body.error)) || `http_${res.status}`)
    if (body && typeof body.error === "string") err.code = body.error
    throw err
  }
  return body
}

// ── Côté public : chargement et submission ─────────────────────────

export async function loadFormToken(token) {
  if (!token) throw new Error("Token requis")
  const { data, error } = await supabase
    .from("form_tokens")
    .select("token, type, logement_id, reservation_id, expires_at, used_at")
    .eq("token", token)
    .maybeSingle()
  if (error) throw error
  if (!data) throw new Error("Token introuvable ou expiré")
  if (data.expires_at && new Date(data.expires_at) < new Date()) {
    throw new Error("Ce lien a expiré.")
  }
  return data
}

export async function submitFormToken(token, payload) {
  // Fiches d'arrivée LOT 2 (ADR-188) — la soumission passe désormais par
  // l'endpoint serveur service_role (action form_submit), qui valide le token,
  // applique une whitelist DYNAMIQUE du payload (clés hors-fields droppées),
  // l'anti-spam, capture l'IP server-side et propage les heures voyageur.
  //
  // L'INSERT anon DIRECT (form_submissions) + l'UPDATE used_at best-effort sont
  // SUPPRIMÉS du client. La policy RLS public_insert reste en base comme défense
  // en profondeur (transition monitorée ; pas de DROP dans ce lot).
  const res = await fetch(
    `/api/beds24-mapping?action=form_submit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, payload }),
    },
  )
  let body = null
  try { body = await res.json() } catch (_) { /* réponse non-JSON (ex. 500 brut) */ }
  if (!res.ok || !body || body.ok !== true) {
    const err = new Error((body && body.error) || "Échec de l'envoi du formulaire")
    // Formulaire déjà reçu (409 'deja_recu'), envoi concurrent en cours (409
    // 'envoi_en_cours') ou vérification impossible (503) : le code permet à la
    // page de basculer sur l'écran « Bien reçu » ou d'afficher le bon message.
    if (body && typeof body.code === 'string') err.code = body.code
    err.status = res.status
    throw err
  }
  return true
}

export function publicFormUrl(token) {
  // LOT 3 (ADR-188) — émet désormais le chemin de marque /bienvenue/<token>.
  // Les anciens liens /forms/<token> déjà distribués restent routés À VIE
  // (alias strict côté index.js + rewrite vercel.json), zéro lien cassé.
  // Lot INFRA 2.1 PR-A — lien DISTRIBUÉ au voyageur : domaine canonique
  // (REACT_APP_FBZ_APP_BASE_URL > dev local > sejour.fullbnbzen.com), plus
  // jamais l'origin du navigateur ni le fallback vercel.app.
  return `${resolveAppBaseUrl()}/bienvenue/${token}`
}
